import { FunctionComponent, useState, useEffect, useRef } from 'react';
import {
    Box, Button, Header, Table, SpaceBetween,
    Modal, FormField, Input, Tabs, Alert, Select, Pagination, CollectionPreferences,
    PropertyFilter, PropertyFilterProps,
} from '@amzn/awsui-components-react';
import { getString, getPropertyFilterString } from 'common/ui-string-labels/ui-strings-utils';
import { buildColumnDefinitions, defaultPagingOptions, buildVisibleContentOptions, buildFilteringProperties } from 'common/utils/table-utils';
import { convertTokensToPropertyFilter } from 'common/utils/property-filter-helper';
import { ColumnSetting } from 'common/utils/config-models';
import { usePreferences } from 'hooks/usePreferences';
import activeTableConfig from './po-active.table-config.json';
import historyTableConfig from './po-history.table-config.json';
import { BUDGET_WARNING_THRESHOLD, BUDGET_CRITICAL_THRESHOLD, VENDORS } from 'configuration/config';
import { VendorPo, POConfigurationTableProps } from './interfaces';
import { POConfigurationService } from 'services/po-configuration-service';

const BULK_VENDORS = VENDORS.filter(v => v.isBulkVendor && v.id !== 'DIR');
const ACTIVE_VISIBLE_OPTIONS = buildVisibleContentOptions(activeTableConfig.columns as ColumnSetting[]);
const HISTORY_VISIBLE_OPTIONS = buildVisibleContentOptions(historyTableConfig.columns as ColumnSetting[]);

/** Filterable properties derived from table config (History's Base PO carries operators: ["="]). */
const HISTORY_FILTERING_PROPERTIES = buildFilteringProperties(historyTableConfig.columns as ColumnSetting[]);

/** Extracts the exact Base PO value from a PropertyFilter query for the server-side filter. */
// exported for tests
export const extractBasePo = (query: PropertyFilterProps.Query): string | undefined =>
    convertTokensToPropertyFilter(query.tokens).basePo?.[0];

// --- Sub-components ---

export interface ActivePOsTableProps {
    items: VendorPo[];
    loading: boolean;
    hasNextPage: boolean;
    currentPage: number;
    onPageChange: (page: number) => void;
    onCreateClick: () => void;
}

/**
 * Displays active POs in a table with pagination and preferences.
 * Receives data from parent (POConfigurationTable) — does not fetch its own data.
 * No filter here: Base PO filtering is only offered on the History table.
 */
export const ActivePOsTable: FunctionComponent<ActivePOsTableProps> = ({ items, loading, hasNextPage, currentPage, onPageChange, onCreateClick }) => {
    const { currentPreferences, onPreferencesConfirmHandler } = usePreferences({
        preferencesKey: 'po-active-preferences',
        defaultPaging: 10,
        columnsSettings: activeTableConfig.columns as ColumnSetting[],
        refreshData: () => {},
    });

    return (
        <Table<VendorPo>
            data-testid="po-configuration-active-table"
            resizableColumns
            variant="full-page"
            loading={loading}
            header={
                <Header
                    variant="h1"
                    
                    actions={<Button data-testid="po-configuration-create-button" variant="primary" onClick={onCreateClick}>{getString('poConfiguration.actions.createNew')}</Button>}
                >
                    {getString('poConfiguration.title')}
                </Header>
            }
            columnDefinitions={buildColumnDefinitions(activeTableConfig.columns as ColumnSetting[], currentPreferences.visibleContent)}
            items={items}
            stripedRows={currentPreferences.stripedRows}
            pagination={
                <Pagination
                    currentPageIndex={currentPage}
                    pagesCount={hasNextPage ? currentPage + 1 : currentPage}
                    openEnd={hasNextPage}
                    onChange={({ detail }) => onPageChange(detail.currentPageIndex)}
                />
            }
            preferences={
                <CollectionPreferences
                    title={getString('common.preferences.title')}
                    confirmLabel={getString('common.preferences.confirmLabel')}
                    cancelLabel={getString('common.preferences.cancelLabel')}
                    preferences={currentPreferences}
                    onConfirm={onPreferencesConfirmHandler}
                    pageSizePreference={{ title: getString('common.preferences.pageSize.title'), options: defaultPagingOptions }}
                    stripedRowsPreference={{ label: getString('common.preferences.stripedRows.label'), description: getString('common.preferences.stripedRows.description') }}
                    visibleContentPreference={{ title: getString('common.preferences.visibleContentPreference.title'), options: [{ label: getString('common.preferences.visibleContentPreference.columns'), options: ACTIVE_VISIBLE_OPTIONS }] }}
                />
            }
            empty={<Box textAlign="center"><b>{getString('poConfiguration.table.emptyActive')}</b></Box>}
        />
    );
};

/**
 * Displays historical (inactive) POs. Self-contained — fetches its own data.
 * Re-fetches when page or pageSize changes (server-side pagination).
 */
export const HistoryPOsTable: FunctionComponent = () => {
    const [items, setItems] = useState<VendorPo[]>([]);
    const [historyNextToken, setHistoryNextToken] = useState<string | null>(null);
    const [historyTokenHistory, setHistoryTokenHistory] = useState<(string | undefined)[]>([undefined]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [historyFilterQuery, setHistoryFilterQuery] = useState<PropertyFilterProps.Query>({ tokens: [], operation: 'and' });
    const { currentPreferences, onPreferencesConfirmHandler } = usePreferences({
        preferencesKey: 'po-history-preferences',
        defaultPaging: 10,
        columnsSettings: historyTableConfig.columns as ColumnSetting[],
        refreshData: () => {},
    });

    useEffect(() => {
        let stale = false;
        setLoading(true);
        const token = historyTokenHistory[currentPage - 1];
        POConfigurationService.instance.getPOs({
            status: false,
            limit: currentPreferences.pageSize!,
            nextToken: token,
            basePo: extractBasePo(historyFilterQuery),
        }).then(data => {
            if (!stale) {
                setItems(data.data);
                setHistoryNextToken(data.nextToken);
                if (data.nextToken) {
                    setHistoryTokenHistory(prev => {
                        const updated = [...prev];
                        updated[currentPage] = data.nextToken!;
                        return updated;
                    });
                }
                setLoading(false);
            }
        }).catch(() => {
            if (!stale) {
                setLoading(false);
            }
        });
        return () => { stale = true; };
    }, [currentPage, currentPreferences.pageSize, historyFilterQuery]);

    // Reset to page 1 when pageSize changes to avoid stale cursors
    const prevPageSize = useRef(currentPreferences.pageSize);
    useEffect(() => {
        if (prevPageSize.current !== currentPreferences.pageSize) {
            prevPageSize.current = currentPreferences.pageSize;
            setHistoryTokenHistory([undefined]);
            setHistoryNextToken(null);
            setCurrentPage(1);
        }
    }, [currentPreferences.pageSize]);

    return (
        <Table<VendorPo> resizableColumns
            data-testid="po-configuration-history-table"
            variant="embedded"
            loading={loading}
            header={<Header variant="h2" >{getString('poConfiguration.table.historyTitle')}</Header>}
            columnDefinitions={buildColumnDefinitions(historyTableConfig.columns as ColumnSetting[], currentPreferences.visibleContent)}
            items={items}
            filter={
                <PropertyFilter
                    data-testid="po-configuration-history-property-filter"
                    query={historyFilterQuery}
                    onChange={({ detail }) => {
                        // Reset cursor pagination together with the filter so React 18 batches
                        // these updates into one render — avoids a stale-cursor fetch.
                        setHistoryFilterQuery(detail);
                        setHistoryTokenHistory([undefined]);
                        setHistoryNextToken(null);
                        setCurrentPage(1);
                    }}
                    filteringProperties={HISTORY_FILTERING_PROPERTIES}
                    i18nStrings={getPropertyFilterString()}
                    filteringPlaceholder={getString('poConfiguration.filter.placeholder')}
                />
            }
            stripedRows={currentPreferences.stripedRows}
            pagination={
                <Pagination
                    currentPageIndex={currentPage}
                    pagesCount={historyNextToken ? currentPage + 1 : currentPage}
                    openEnd={historyNextToken !== null}
                    onChange={({ detail }) => setCurrentPage(detail.currentPageIndex)}
                />
            }
            preferences={
                <CollectionPreferences
                    title={getString('common.preferences.title')}
                    confirmLabel={getString('common.preferences.confirmLabel')}
                    cancelLabel={getString('common.preferences.cancelLabel')}
                    preferences={currentPreferences}
                    onConfirm={onPreferencesConfirmHandler}
                    pageSizePreference={{ title: getString('common.preferences.pageSize.title'), options: defaultPagingOptions }}
                    stripedRowsPreference={{ label: getString('common.preferences.stripedRows.label'), description: getString('common.preferences.stripedRows.description') }}
                    visibleContentPreference={{ title: getString('common.preferences.visibleContentPreference.title'), options: [{ label: getString('common.preferences.visibleContentPreference.columns'), options: HISTORY_VISIBLE_OPTIONS }] }}
                />
            }
            empty={<Box textAlign="center"><b>{getString('poConfiguration.table.emptyHistory')}</b></Box>}
        />
    );
};

// --- Main component ---

/**
 * Main PO Configuration component. Manages:
 * - Active POs fetch (data shared with alerts + ActivePOsTable)
 * - Budget threshold alerts (warning at 85%, critical at 100%)
 * - Tab navigation between Active and History
 * - Create PO modal
 */
export const POConfigurationTable: FunctionComponent<POConfigurationTableProps> = ({ useFlashBarItemsActions }) => {
    const [activePOs, setActivePOs] = useState<VendorPo[]>([]);
    const [activeNextToken, setActiveNextToken] = useState<string | null>(null);
    const [activeTokenHistory, setActiveTokenHistory] = useState<(string | undefined)[]>([undefined]);
    const [loadingActive, setLoadingActive] = useState(true);
    const [activeCurrentPage, setActiveCurrentPage] = useState(1);
    const [refreshKey, setRefreshKey] = useState(0);
    const [activeTab, setActiveTab] = useState('active');
    const [createModalVisible, setCreateModalVisible] = useState(false);

    // @todo: Create PO form — will call backend on submit
    const [newBasePo, setNewBasePo] = useState('');
    const [newBudget, setNewBudget] = useState('');
    const [newVendor, setNewVendor] = useState('');

    // Re-fetch active POs when page changes
    useEffect(() => {
        let stale = false;
        setLoadingActive(true);
        const token = activeTokenHistory[activeCurrentPage - 1];
        POConfigurationService.instance.getPOs({
            status: true,
            limit: 10,
            nextToken: token,
        }).then(data => {
            if (!stale) {
                setActivePOs(data.data);
                setActiveNextToken(data.nextToken);
                if (data.nextToken) {
                    setActiveTokenHistory(prev => {
                        const updated = [...prev];
                        updated[activeCurrentPage] = data.nextToken!;
                        return updated;
                    });
                }
                setLoadingActive(false);
            }
        }).catch(() => {
            if (!stale) {
                useFlashBarItemsActions.addErrorFlashBarItem(getString('poConfiguration.flashbar.error') || 'An error occurred. Please try again.');
                setLoadingActive(false);
            }
        });
        return () => { stale = true; };
    }, [activeCurrentPage, refreshKey]);

    // Alerts derived from active POs — always available since this is the first fetch
    const criticalPOs = activePOs.filter(p => p.budgetUsed / p.budgetLimit >= BUDGET_CRITICAL_THRESHOLD / 100);
    const warningPOs = activePOs.filter(p => {
        const ratio = p.budgetUsed / p.budgetLimit;
        return ratio >= BUDGET_WARNING_THRESHOLD / 100 && ratio < BUDGET_CRITICAL_THRESHOLD / 100;
    });

    // @todo: Will call backend to create PO, then refresh active POs
    const resetForm = () => {
        setNewBasePo('');
        setNewBudget('');
        setNewVendor('');
    };

    const closeModal = () => {
        setCreateModalVisible(false);
        resetForm();
    };

    const [creating, setCreating] = useState(false);
    const [confirmCreate, setConfirmCreate] = useState(false);

    const handleCreateClick = () => {
        if (!newBasePo || !newBudget || !newVendor) return;
        setCreateModalVisible(false);
        setConfirmCreate(true);
    };

    const handleConfirmBack = () => {
        setConfirmCreate(false);
        setCreateModalVisible(true);
    };

    const createNewPo = () => {
        setCreating(true);
        POConfigurationService.instance.createPO({
            vendor: newVendor,
            basePo: newBasePo,
            budgetLimit: Number(newBudget),
        }).then((po) => {
            useFlashBarItemsActions.addSuccessFlashBarItem(`New PO ${po.basePo} created for ${po.vendor}`);
            setActiveCurrentPage(1);
            setRefreshKey((k) => k + 1);
        }).catch((err) => {
            useFlashBarItemsActions.addErrorFlashBarItem(err.message || getString('poConfiguration.flashbar.error') || 'An error occurred');
        }).finally(() => {
            setCreating(false);
            setConfirmCreate(false);
            closeModal();
        });
    };

    return (
        <>
            <SpaceBetween size="l">
                {criticalPOs.length > 0 && (
                    <Alert type="error" header={getString('poConfiguration.alerts.criticalHeader')}>
                        {criticalPOs.map(p => `${p.vendor}: ${p.basePo} at ${Math.round(p.budgetUsed / p.budgetLimit * 100)}%`).join(', ')}
                        {` — ${getString('poConfiguration.alerts.criticalAction')}`}
                    </Alert>
                )}
                {warningPOs.length > 0 && (
                    <Alert type="warning" header={getString('poConfiguration.alerts.warningHeader')}>
                        {warningPOs.map(p => `${p.vendor}: ${p.basePo} at ${Math.round(p.budgetUsed / p.budgetLimit * 100)}%`).join(', ')}
                        {` — ${getString('poConfiguration.alerts.warningAction')}`}
                    </Alert>
                )}

                <Tabs
                    activeTabId={activeTab}
                    onChange={({ detail }) => setActiveTab(detail.activeTabId)}
                    tabs={[
                        {
                            id: 'active',
                            label: getString('poConfiguration.tabs.active'),
                            content: (
                                <ActivePOsTable
                                    items={activePOs}
                                    loading={loadingActive}
                                    hasNextPage={activeNextToken !== null}
                                    currentPage={activeCurrentPage}
                                    onPageChange={setActiveCurrentPage}
                                    onCreateClick={() => setCreateModalVisible(true)}
                                />
                            ),
                        },
                        {
                            id: 'history',
                            label: getString('poConfiguration.tabs.history'),
                            content: <HistoryPOsTable />,
                        },
                    ]}
                />
            </SpaceBetween>

            <Modal
                data-testid="create-po-modal"
                visible={createModalVisible}
                onDismiss={closeModal}
                header={getString('poConfiguration.createModal.title')}
                footer={
                    <Box float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button data-testid="create-po-cancel" onClick={closeModal}>{getString('common.cancel')}</Button>
                            <Button data-testid="create-po-submit" variant="primary" onClick={handleCreateClick} disabled={!newVendor || !newBasePo || !newBudget || creating} loading={creating}>{getString('poConfiguration.createModal.submit')}</Button>
                        </SpaceBetween>
                    </Box>
                }
            >
                <SpaceBetween size="m">
                    <FormField label={getString('poConfiguration.createModal.vendor')}>
                        <Select
                            data-testid="create-po-vendor-select"
                            selectedOption={newVendor ? { value: newVendor, label: VENDORS.find(v => v.id === newVendor)?.label ?? newVendor } : null}
                            onChange={({ detail }) => setNewVendor(detail.selectedOption.value!)}
                            options={BULK_VENDORS.map(v => ({ value: v.id, label: v.label }))}
                            placeholder="Select vendor"
                            selectedAriaLabel="Selected"
                        />
                    </FormField>
                    <FormField label={getString('poConfiguration.createModal.basePo')} description={(() => {
                        const activePoForVendor = activePOs.find(p => p.vendor === newVendor);
                        return activePoForVendor
                            ? `Current active PO: ${activePoForVendor.basePo} (don't add consecutive, it will be handled automatically)`
                            : getString('poConfiguration.createModal.basePoDescription');
                    })()}>
                        <Input data-testid="create-po-base-po-input" value={newBasePo} onChange={({ detail }) => setNewBasePo(detail.value)} placeholder="e.g. 2D-19717878" />
                    </FormField>
                    <FormField label={getString('poConfiguration.createModal.budgetLimit')}>
                        <Input data-testid="create-po-budget-input" value={newBudget} type="number" onChange={({ detail }) => setNewBudget(detail.value)} placeholder="e.g. 500000" />
                    </FormField>
                    <Box color="text-status-inactive" fontSize="body-s">
                        {getString('poConfiguration.createModal.info')}
                    </Box>
                </SpaceBetween>
            </Modal>

            <Modal
                data-testid="confirm-create-po-modal"
                visible={confirmCreate}
                onDismiss={handleConfirmBack}
                header={getString('poConfiguration.createModal.confirmHeader')}
                footer={
                    <Box float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button data-testid="confirm-create-po-back" onClick={handleConfirmBack}>{getString('common.back')}</Button>
                            <Button data-testid="confirm-create-po-confirm" variant="primary" onClick={createNewPo} disabled={creating} loading={creating}>{getString('poConfiguration.createModal.confirm')}</Button>
                        </SpaceBetween>
                    </Box>
                }
            >
                {getString('poConfiguration.createModal.confirmMessage', { basePo: newBasePo, vendor: newVendor, budget: Number(newBudget).toLocaleString() })}
                {activePOs.find(p => p.vendor === newVendor) && (
                    <Box variant="p" color="text-status-warning" margin={{ top: 's' }}>
                        {getString('poConfiguration.createModal.deactivateWarning', { currentPo: activePOs.find(p => p.vendor === newVendor)!.basePo, vendor: newVendor })}
                    </Box>
                )}
            </Modal>
        </>
    );
};
